// client\src\App.js

import React, {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";

import RequireAuth from "./requireAuth.js";
import GlobalSeo from "./seo/GlobalSeo";
import ToastConfirm from "./dashboard/components/ToastConfirm.js";
import CenterToastHost from "./dashboard/components/CenterToastHost.js";
import "./CookieConsentBanner.css";

/* ------------------------- Ленивые страницы ------------------------- */

const Main = lazy(() => import("./main/Main.js"));
const MerchPage = lazy(() => import("./main/MerchPage.js"));

const InteractivePage = lazy(() => import("./interactive/InteractivePage.js"));
const InteractiveTasksPage = lazy(
  () => import("./interactive/InteractiveTasksPage.js"),
);

const PrivacyPage = lazy(
  () => import("./auth/pages/documentsPages/PrivacyPage.js"),
);
const PersonalDataConsentPage = lazy(
  () => import("./auth/pages/documentsPages/PersonalDataConsentPage.js"),
);
const StudentRegistrationConsentPage = lazy(
  () => import("./auth/pages/documentsPages/StudentRegistrationConsentPage.js"),
);

const TutorRegistrationConsentPage = lazy(
  () => import("./auth/pages/documentsPages/TutorRegistrationConsentPage.js"),
);

const ContactConsentPage = lazy(
  () => import("./auth/pages/documentsPages/ContactConsentPage.js"),
);
const PrivacyAppPage = lazy(
  () => import("./auth/pages/documentsPages/PrivacyAppPage.js"),
);
const AppConsentPage = lazy(
  () => import("./auth/pages/documentsPages/AppConsentPage.js"),
);

const AppOfferPage = lazy(
  () => import("./auth/pages/documentsPages/AppOfferPage.js"),
);

const ContactPage = lazy(() => import("./auth/pages/ContactPage.js"));
const NotFoundPage = lazy(() => import("./main/NotFoundPage.js"));
const TechnicalWorkPage = lazy(() => import("./main/TechnicalWorkPage.js"));
const PaymentAndRefundPage = lazy(
  () => import("./auth/pages/documentsPages/PaymentAndRefundPage.js"),
);
const DeliveryPage = lazy(
  () => import("./auth/pages/documentsPages/DeliveryPage.js"),
);

const RegisterPage = lazy(() => import("./auth/pages/RegisterPage.js"));
const VerifyEmailPage = lazy(() => import("./auth/pages/VerifyEmailPage.js"));
const LoginPage = lazy(() => import("./auth/pages/LoginPage.js"));
const ResetPasswordPage = lazy(
  () => import("./auth/pages/ResetPasswordPage.js"),
);
const TutorLoginPage = lazy(() => import("./auth/pages/TutorLoginPage.js"));
const TutorRegisterPage = lazy(
  () => import("./auth/pages/TutorRegisterPage.js"),
);
const TutorResetPasswordPage = lazy(
  () => import("./auth/pages/TutorResetPasswordPage.js"),
);
const DeleteAccountPage = lazy(
  () => import("./auth/pages/DeleteAccountPage.js"),
);
const TutorDeleteAccountPage = lazy(
  () => import("./auth/pages/TutorDeleteAccountPage.js"),
);
const TermsStudentsPage = lazy(
  () => import("./auth/pages/documentsPages/TermsStudentsPage.js"),
);
const TermsTutorsPage = lazy(
  () => import("./auth/pages/documentsPages/TermsTutorsPage.js"),
);
const RequisitesPage = lazy(() => import("./auth/pages/RequisitesPage.js"));

const TouristsExpressLanding = lazy(
  () => import("./main/TouristsExpressLanding"),
);
const TouristsLanding = lazy(() => import("./main/TouristsLanding.js"));
const TouristsFullLanding = lazy(() => import("./main/TouristsFullLanding"));
const AdultsLanding = lazy(() => import("./main/AdultsLanding"));

const PreschoolLanding = lazy(() => import("./main/PreschoolLanding.js"));
const PrimaryLanding = lazy(() => import("./main/PrimaryLanding.js"));

const PartnersPage = lazy(() => import("./main/PartnersPage"));

const MeilinAppsPage = lazy(() => import("./main/MeilinAppsPage"));

const ArticlesPage = lazy(() => import("./articles/ArticlesPage"));
const ArticlePage = lazy(() => import("./articles/ArticlePage"));

const StudentDashboard = lazy(
  () => import("./dashboard/student/StudentDashboard.js"),
);
const CertificateVerifyPage = lazy(
  () => import("./dashboard/student/CertificateVerifyPage.js"),
);
const CourseSurveyPage = lazy(
  () => import("./dashboard/student/CourseSurveyPage.js"),
);

const CoursePage = lazy(() => import("./course/coursePages/CoursePage.js"));
const LessonVideoPage = lazy(
  () => import("./course/coursePages/LessonVideoPage.js"),
);
const LessonTasksPage = lazy(
  () => import("./course/coursePages/LessonTasksPage.js"),
);
const TaskSuccessPage = lazy(
  () => import("./course/coursePages/TaskSuccessPage.js"),
);
const CourseIntroPage = lazy(
  () => import("./course/coursePages/CourseIntroPage.js"),
);

const WalletReturnPage = lazy(
  () => import("./dashboard/wallet/WalletReturnPage.js"),
);
const CourseReturnPage = lazy(
  () => import("./dashboard/wallet/CourseReturnPage.js"),
);
const MerchReturnPage = lazy(
  () => import("./dashboard/wallet/MerchReturnPage.js"),
);

const TutorDashboard = lazy(
  () => import("./dashboard/tutor/TutorDashboard.js"),
);
const TutorSheetsGenerator = lazy(
  () => import("./dashboard/tutor/TutorSheetsGenerator.js"),
);
const TutorGuidePage = lazy(
  () => import("./dashboard/tutor/TutorGuidePage.js"),
);

const AdminPage = lazy(() => import("./admin/AdminPage.js"));
const ReviewPage = lazy(() => import("./main/ReviewPage.js"));
/* ------------------------- Ленивые задания ------------------------- */

const MemoryTask = lazy(() => import("./tasks/MemoryTask/MemoryTask.js"));
const FindTranslation = lazy(
  () => import("./tasks/games/ChoiceTask/FindTranslation.js"),
);
const FindPinyin = lazy(() => import("./tasks/games/ChoiceTask/FindPinyin.js"));
const FindByImage = lazy(
  () => import("./tasks/games/ChoiceTask/FindByImage.js"),
);
const DownloadSheets = lazy(
  () => import("./tasks/DownloadSheets/DownloadSheets.js"),
);
const PaintingHanzi = lazy(
  () => import("./tasks/PaintingHanzi/PaintingHanzi.js"),
);
const AudioToWord = lazy(
  () => import("./tasks/games/AudioTask/AudioToWord.js"),
);
const ImageToAudioNumber = lazy(
  () => import("./tasks/games/AudioTask/ImageToAudioNumber.js"),
);
const HanziToAudioNumber = lazy(
  () => import("./tasks/games/AudioTask/HanziToAudioNumber.js"),
);
const PinyinFromAudio = lazy(
  () => import("./tasks/games/AudioTask/PinyinFromAudio.js"),
);
const ListenAndRepeat = lazy(
  () => import("./tasks/ListenAndRepeat/ListenAndRepeat.js"),
);
const MakeSentence = lazy(
  () => import("./tasks/games/DnDOrderTask/MakeSentence.js"),
);
const PinyinOrder = lazy(
  () => import("./tasks/games/DnDOrderTask/PinyinOrder.js"),
);
const ToneFromAudio = lazy(
  () => import("./tasks/games/DnDOrderTask/ToneFromAudio.js"),
);

const taskComponents = [
  { title: "Мемори", path: "MemoryTask", component: MemoryTask },
  {
    title: "Найди перевод",
    path: "FindTranslation",
    component: FindTranslation,
  },
  { title: "Найди пиньинь", path: "FindPinyin", component: FindPinyin },
  {
    title: "Найди слово по картинке",
    path: "FindByImage",
    component: FindByImage,
  },
  {
    title: "Скачай прописи",
    path: "DownloadSheets",
    component: DownloadSheets,
  },
  { title: "Пиши иероглиф", path: "PaintingHanzi", component: PaintingHanzi },
  { title: "Слово по аудио", path: "AudioToWord", component: AudioToWord },
  {
    title: "Аудио по картинке",
    path: "ImageToAudioNumber",
    component: ImageToAudioNumber,
  },
  {
    title: "Аудио по иероглифу",
    path: "HanziToAudioNumber",
    component: HanziToAudioNumber,
  },
  {
    title: "Пиньинь по аудио",
    path: "PinyinFromAudio",
    component: PinyinFromAudio,
  },
  {
    title: "Послушай и повтори",
    path: "ListenAndRepeat",
    component: ListenAndRepeat,
  },

  {
    title: "Составь предложение",
    path: "MakeSentence",
    component: MakeSentence,
  },
  { title: "Порядок пиньинь", path: "PinyinOrder", component: PinyinOrder },
  {
    title: "Выбери тон по аудио",
    path: "ToneFromAudio",
    component: ToneFromAudio,
  },
];

function YandexMetrikaTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!window.ym) return;

    window.ym(
      108716109,
      "hit",
      location.pathname + location.search + location.hash,
    );
  }, [location.pathname, location.search, location.hash]);

  return null;
}
function CookieConsentBanner() {
  const [cookieChoice, setCookieChoice] = useState(() => {
    try {
      return localStorage.getItem("cookieAccepted");
    } catch {
      return null;
    }
  });

  if (cookieChoice !== null) return null;

  const saveChoice = (value) => {
    try {
      localStorage.setItem("cookieAccepted", value);
    } catch {}

    setCookieChoice(value);

    if (value === "true") {
      window.loadYandexMetrika?.();
    }
  };

  return (
    <div
      className="cookie-banner"
      role="dialog"
      aria-label="Настройки файлов cookie"
    >
      <div className="cookie-banner__text">
        Мы используем файлы cookie и российскую систему веб-аналитики
        «Яндекс.Метрика» только с вашего согласия. Подробнее — в{" "}
        <a href="/privacy" target="_blank" rel="noopener noreferrer">
          Политике конфиденциальности
        </a>
        .
      </div>

      <div className="cookie-banner__actions">
        <button
          type="button"
          className="cookie-banner__btn cookie-banner__btn--accept"
          onClick={() => saveChoice("true")}
        >
          Принять
        </button>

        <button
          type="button"
          className="cookie-banner__btn cookie-banner__btn--reject"
          onClick={() => saveChoice("false")}
        >
          Отклонить
        </button>
      </div>
    </div>
  );
}
/**
 * PublicTrialGate — обёртка для уроков:
 * если lessonNumber > 1 → требуем авторизацию, иначе пропускаем гостья.
 * Никаких доп. запросов: проверку куки делает RequireAuth.
 */
function PublicTrialGate({ children }) {
  const { lessonNumber } = useParams();
  const n = Number(lessonNumber);
  const needsAuth = Number.isFinite(n) && n > 1;
  return needsAuth ? (
    <RequireAuth role="student">{children}</RequireAuth>
  ) : (
    <>{children}</>
  );
}
function RouteBodyClassSync() {
  const location = useLocation();

  useLayoutEffect(() => {
    const path = location.pathname.replace(/\/+$/, "") || "/";

    if (path !== "/") {
      document.documentElement.classList.add("not-home-route");
    } else {
      document.documentElement.classList.remove("not-home-route");
    }
  }, [location.pathname]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, [pathname]);

  return null;
}
// function RouteFallback() {
//   console.log("MEILIN_BUILD_CHECK_2026_06_07_ROUTE_FALLBACK");

//   return (
//     <div className="app-route-loading">
//       <Loader fullscreen={false} label="Загрузка..." size={72} />
//     </div>
//   );
// }

export default function App() {
  console.log("MEILIN_NO__APP_2026_06_07");

  useLayoutEffect(() => {
    document.documentElement.classList.remove("app-booting");
    document.documentElement.classList.add("app-ready");
  }, []);

  return (
    <Suspense fallback={null}>
      <Router>
        <ScrollToTop />
        <RouteBodyClassSync />
        <GlobalSeo />

        <YandexMetrikaTracker />
        <CookieConsentBanner />

        <ToastConfirm standalone />

        <Routes>
          {/* Лендинги — выше главной */}
          <Route path="/lp/adults" element={<AdultsLanding />} />

          <Route path="/preschool" element={<PreschoolLanding />} />
          <Route path="/primary-school" element={<PrimaryLanding />} />
          <Route path="/lp/tourists-full" element={<TouristsFullLanding />} />
          <Route path="/lp/tourists" element={<TouristsLanding />} />

          <Route
            path="/lp/tourists-express"
            element={<TouristsExpressLanding />}
          />
          <Route path="/review" element={<ReviewPage />} />
          <Route path="/course/:courseId/intro" element={<CourseIntroPage />} />
          {/* Публичные */}
          <Route path="/" element={<Main />} />
          <Route path="/merch" element={<MerchPage />} />
          <Route path="/merch/:categorySlug" element={<MerchPage />} />
          <Route path="/interactive" element={<InteractivePage />} />
          <Route path="/apps" element={<MeilinAppsPage />} />

          <Route
            path="/interactive/:categorySlug"
            element={<InteractivePage />}
          />
          <Route
            path="/interactive/:categorySlug/:productSlug"
            element={<InteractiveTasksPage />}
          />

          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/:slug" element={<ArticlePage />} />

          <Route
            path="/certificate/:certificateId"
            element={<CertificateVerifyPage />}
          />

          <Route path="/terms-students" element={<TermsStudentsPage />} />
          <Route path="/terms-tutors" element={<TermsTutorsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route
            path="/consent-personal-data"
            element={<PersonalDataConsentPage />}
          />
          <Route
            path="/consent-registration-student"
            element={<StudentRegistrationConsentPage />}
          />

          <Route
            path="/consent-registration-tutor"
            element={<TutorRegistrationConsentPage />}
          />

          <Route path="/consent-contact" element={<ContactConsentPage />} />
          <Route path="/privacy-app" element={<PrivacyAppPage />} />
          <Route path="/consent-app" element={<AppConsentPage />} />
          <Route path="/offer-app" element={<AppOfferPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/requisites" element={<RequisitesPage />} />
          <Route path="/payment-refund" element={<PaymentAndRefundPage />} />
          <Route path="/delivery" element={<DeliveryPage />} />
          <Route path="/payment/success" element={<WalletReturnPage />} />
          <Route
            path="/payment/course-success"
            element={<CourseReturnPage />}
          />
          <Route path="/payment/course-return" element={<CourseReturnPage />} />
          <Route path="/payment/merch-return" element={<MerchReturnPage />} />

          {/* Auth */}
          <Route path="/register" element={<RegisterPage role="student" />} />
          <Route path="/auth/verify" element={<VerifyEmailPage />} />
          <Route path="/auth/login" element={<LoginPage role="student" />} />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage role="student" />}
          />
          <Route
            path="/auth/reset-password"
            element={<ResetPasswordPage role="student" />}
          />
          <Route path="/auth/tutor/login" element={<TutorLoginPage />} />
          <Route path="/auth/tutor/register" element={<TutorRegisterPage />} />
          <Route
            path="/auth/tutor/reset-password"
            element={<TutorResetPasswordPage />}
          />
          <Route
            path="/auth/delete"
            element={<DeleteAccountPage role="student" />}
          />
          <Route
            path="/auth/tutor/delete"
            element={<TutorDeleteAccountPage />}
          />

          {/* Приватные: ученик */}
          <Route
            path="/student/dashboard"
            element={
              <RequireAuth role="student">
                <StudentDashboard />
              </RequireAuth>
            }
          />

          {/* Админка */}
          <Route
            path="/admin"
            element={
              <RequireAuth role="admin">
                <AdminPage />
              </RequireAuth>
            }
          />
          <Route path="/partners" element={<PartnersPage />} />
          {/* Курсы */}
          <Route path="/course/:courseId" element={<CoursePage />} />
          <Route path="/course/survey" element={<CourseSurveyPage />} />

          <Route
            path="/student/course/survey"
            element={<Navigate to="/course/survey" replace />}
          />

          <Route
            path="/course/:courseId/:lessonNumber/video"
            element={
              <PublicTrialGate>
                <LessonVideoPage />
              </PublicTrialGate>
            }
          />

          <Route
            path="/course/:courseId/:lessonNumber/tasks"
            element={
              <PublicTrialGate>
                <LessonTasksPage />
              </PublicTrialGate>
            }
          />

          <Route
            path="/course/:courseId/:lessonNumber/task/:taskId/success"
            element={
              <PublicTrialGate>
                <TaskSuccessPage />
              </PublicTrialGate>
            }
          />

          {taskComponents.map(({ path, component: Component }) => (
            <Route
              key={path}
              path={`/course/:courseId/:lessonNumber/task/${path}`}
              element={
                <PublicTrialGate>
                  <Component />
                </PublicTrialGate>
              }
            />
          ))}

          {/* Приватные: репетитор */}
          <Route
            path="/tutor/dashboard"
            element={
              <RequireAuth role="tutor">
                <TutorDashboard />
              </RequireAuth>
            }
          />

          <Route
            path="/tutor/guide"
            element={
              <RequireAuth role="tutor">
                <TutorGuidePage />
              </RequireAuth>
            }
          />

          <Route
            path="/tutor/sheets"
            element={
              <RequireAuth role="tutor">
                <TutorSheetsGenerator />
              </RequireAuth>
            }
          />

          <Route path="/maintenance" element={<TechnicalWorkPage />} />

          {/* Fallback */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        <CenterToastHost />

        {/* Global rotate overlay */}
        {/* <div className="global-rotate-overlay" aria-hidden="true">
        <div className="global-rotate-box">
          <div className="global-rotate-icon">📱↻</div>
          <div className="global-rotate-text">
            Пожалуйста, переверните телефон вертикально
          </div>
        </div>
      </div> */}
      </Router>
    </Suspense>
  );
}
